import { SeasonTrayFormViewModel } from './../season-tray-form/season-tray-form.view-model';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { isTruthy, PlatformType } from 'osn-shared';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { RootAppState } from '../../../app.reducer';
import {
  selectCxbCountries,
  selectCxbPlatforms,
  selectCxbSelectedTenant,
  selectCxbUserSegments,
  selectGenres,
} from '../../../app.selectors';
import { Country } from '../../../domain/country';
import { PageItem } from '../../../domain/page-item';
import { PageVersion } from '../../../domain/page-version';
import { Platform } from '../../../domain/platform';
import { UserSegment, UserSegmentType } from '../../../domain/user-segment';
import { loadCxbPageEditScreen } from '../../../page-edit-screen/page-edit-screen.actions';
import { AppState as PageEditAppState } from '../../../page-edit-screen/page-edit-screen.reducer';
import { selectCxbPageVersion } from '../../../page-edit-screen/page-edit-screen.selectors';
import { PageType } from '../../../page-edit-screen/page-type';
import { TrayTypeBridge } from '../../../page-edit-screen/tray-type-bridge';
import * as PageItemPreviewActions from '../../../page-item-preview/page-item-preview.actions';
import { FeatureState as PreviewFeatureState } from '../../../page-item-preview/page-item-preview.reducer';
import { RouterState, selectRouteParam } from '../../../router.selectors';
import { MultiSelectOption } from '../../../shared/components/multi-select/multi-select.component';
import { UserSegmentLabel } from '../../common/user-segment-label';
import {
  clearCxbCreateTrayScreenState,
  createCxbPageItem,
  deleteCxbPageItem,
  loadCxbPageItem,
  updateCxbPageItem,
} from '../../create-tray-screen.actions';
import { AppState } from '../../create-tray-screen.reducer';
import { selectCxbPageItem } from '../../create-tray-screen.selectors';
import { TrayFormViewModel } from '../view-model-common/tray-form-view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { CreateTrayScreenViewModel } from './create-tray-screen.view-model';
import { Genre } from '../../../domain/genre';
import { CollectionTrayFormViewModel } from '../collection-tray-form/collection-tray-form-view-model';
import {
  mapHeroSpotlightTrayDomainModelToViewModel,
  mapHeroSpotlightTrayViewModelToDomainModel,
  mapHeroTrayDomainModelToViewModel,
  mapHeroTrayViewModelToDomainModel,
} from '../hero-editor/hero-editor.mapping';
import { mapDomainCTAToViewModel, mapViewModelCtaToDomain } from '../marketing-cta-tray-form/marketing-cta-tray-form.mapping';
import { mapDomainMarketingUspToViewModel, mapViewModelUspToDomain } from '../marketing-usp-tray-form/marketing-usp-tray-form.mapping';
import {
  mapDomainMarketingHeroToViewModel,
  mapViewModelMarketingHeroToDomain,
} from '../marketing-hero-tray-form/marketing-hero-tray-form.mapping';
import {
  mapDomainDetailedPageMarketingBannerToViewModel,
  mapViewModelDetailedPageMarketingBannerToDomain,
} from '../detailed-page-marketing-banner-form/detailed-page-marketing-banner-form.mapper';
import {
  mapDomainRedeemPartnerVoucherTrayToViewModel,
  mapViewModelRedeemPartnerVoucherTrayToDomain,
} from '../redeem-partner-voucher-tray-form/redeem-partner-voucher-tray-form.mapping';
import {
  mapDomainRedeemGenericVoucherTrayToViewModel,
  mapViewModelRedeemGenericVoucherTrayToDomain,
} from '../redeem-generic-voucher-tray-form/redeem-generic-voucher-tray-form.mapping';
import { mapCommonTrayDomainModelToViewModel, mapCommonTrayViewModelToDomainModel } from '../common-tray-form/common-tray-form.mapping';
import { mapDomainFaqTrayToViewModel, mapViewModelFaqTrayToDomain } from '../faq-tray-form/faq-tray-form.mapping';
import {
  mapCollectionTrayDomainModelToViewModel,
  mapCollectionTrayViewModelToDomainModel,
} from '../collection-tray-form/collection-tray-form.mapping';
import { mapSeasonTrayDomainModelToViewModel, mapSeasonTrayViewModelToDomainModel } from '../season-tray-form/season-tray-form.mapping';
import {
  mapSinglePhotoSeasonTrayDomainModelToViewModel,
  mapSinglePhotoSeasonTrayViewModelToDomainModel,
} from '../single-photo-season-tray-form/single-photo-season-tray-from.mapping';
import {
  mapBuiltInTrayDomainModelToViewModel,
  mapBuiltInTrayFormViewModelToDomainModel,
  mapContinueWatchingOndemandTrayViewModelToDomainModel,
  mapContinueWatchingTrayViewModelToDomainModel,
  mapFavouriteChannelsTrayViewModelToDomainModel,
  mapLiveTVRemindersTrayViewModelToDomainModel,
  mapMyListTrayViewModelToDomainModel,
  mapTVGuideTrayViewModelToDomainModel,
  mapWatchlistOnDemandTrayViewModelToDomainModel,
  mapPrimeVideoRecommendationsTrayViewModelToDomainModel,
} from '../built-in-tray-form/built-in-tray-form.mapping';
import {
  mapRecentlyWatchedTVTrayDomainModelToViewModel,
  mapRecentlyWatchedTVTrayViewModelToDomainModel,
} from '../recently-watched-tv-tray-form/recently-watched-tv-tray-form.mapping';
import {
  mapCurrentlyOnTvTrayDomainModelToViewModel,
  mapCurrentlyOnTvTrayViewModelToDomainModel,
} from '../currently-on-tv-tray-form/currently-on-tv-tray-form.mapping';
import {
  mapChannelLogoTrayDomainModelToViewModel,
  mapChannelLogoTrayViewModelToDomainModel,
} from '../channel-logo-tray-form/channel-logo-tray-form.mapping';
import {
  mapTitleImagePortraitTrayDomainModelToViewModel,
  mapTitleImagePortraitTrayViewModelToDomainModel,
} from '../title-image-portrait-tray-form/title-image-portrait-tray-form.mapping';
import {
  mapSearchTagsTrayDomainModelToViewModel,
  mapSearchTagsTrayViewModelToDomainModel,
} from '../search-tags-tray/search-tags-tray.mapping';
import {
  mapStreamingContentTrayDomainModelToViewModel,
  mapStreamingContentTrayViewModelToDomainModel,
} from '../streaming-content-tray-form/streaming-content-tray-form.mapping';
import {
  mapTopTenOnLiveTvTrayDomainModelToViewModel,
  mapTopTenOnLiveTvTrayViewModelToDomainModel,
} from '../top-ten-on-live-tv-tray-form/top-ten-on-live-tv-tray-form.mapping';
import {
  mapComingSoonForLiveTvTrayDomainModelToViewModel,
  mapComingSoonForLiveTvTrayViewModelToDomainModel,
} from '../coming-soon-for-live-tv-tray/coming-soon-for-live-tv-tray.mapping';
import {
  mapBannersTrayWithCtaTrayDomainModelToViewModel,
  mapBannersTrayWithCtaTrayViewModelToDomainModel,
} from '../banners-tray-with-cta/banners-tray-with-cta.mapping';
import {
  mapTopTenOnOSNPlusPosterArtTrayDomainModelToViewModel,
  mapTopTenOnOSNPlusPosterArtTrayViewModelToDomainModel,
} from '../top-ten-on-osn-plus-poster-art-tray-form/top-ten-on-osn-plus-poster-art-tray-form.mapping';
import {
  mapUpcomingOnSpecificTvChannelTrayDomainModelToViewModel,
  mapUpcomingOnSpecificTvChannelTrayViewModelToDomainModel,
} from '../upcoming-on-specific-tv-channel-tray-form/upcoming-on-specific-tv-channel-tray-form.mapping';
import {
  mapRecommendedChannelsTrayDomainModelToViewModel,
  mapRecommendedChannelsTrayViewModelToDomainModel,
} from '../recommended-channels-tray/recommended-channels-tray.mapping';
import {
  mapMarketingBannerTrayDomainModelToViewModel,
  mapMarketingBannerTrayViewModelToDomainModel,
} from '../marketing-banner-tray-form/marketing-banner-tray-form.mapping';
import {
  mapRecommendationTrayDomainModelToViewModel,
  mapRecommendationTrayViewModelToDomainModel,
} from '../recommendation-tray-form/recommendation-tray-form.mapping';
import {
  mapAppsAndGamesInstalledAppsTrayDomainModelToViewModel,
  mapAppsAndGamesRecommendedAppsTrayDomainModelToViewModel,
  mapAppsAndGamesTrayViewModelToDomainModel,
} from '../apps-and-games-tray-form/apps-and-games-tray-form.mapping';
import {
  mapFullscreenTrayDomainModelToViewModel,
  mapFullscreenTrayViewModelToDomainModel,
} from '../fullscreen-tray-form/fullscreen-tray-form.mapping';
import {
  mapNewCarouselTrayDomainModelToViewModel,
  mapNewCarouselTrayViewModelToDomainModel,
} from '../new-carousel-tray-form/new-carousel-tray-form.mapping';
import {
  mapCarouselBannerTrayDomainModelToViewModel,
  mapCarouselBannerTrayViewModelToDomainModel,
} from '../carousel-banner-tray-form/carousel-banner-tray-form.mapping';
import {
  mapMarketingEmailBlockerTrayDomainModelToViewModel,
  mapMarketingEmailBlockerTrayViewModelToDomainModel,
} from '../marketing-email-blocker-tray-form/marketing-email-blocker-tray-form.mapping';
import {
  mapSpotlightTrayDomainModelToViewModel,
  mapSpotlightTrayViewModelToDomainModel,
} from '../spotlight-tray-form/spotlight-tray-form.mapping';
import {
  mapDomainGenreTrayToViewModel,
  mapDomainStudiosTrayToViewModel,
  mapViewModelGenreTrayToDomain,
  mapViewModelStudiosTrayToDomain,
} from '../generic-tray-form/generic-tray-form.mapping';
import { mapDomainRedeemUspTrayToViewModel, mapViewModelRedeemUspTrayToDomain } from '../redeem-usp-tray-form/redeem-usp-tray-form.mapping';
import { mapDomainRedeemCtaTrayToViewModel, mapViewModelRedeemCtaTrayToDomain } from '../redeem-cta-tray-form/redeem-cta-tray-form.mapping';
import {
  mapDomainDeviceModuleTrayToViewModel,
  mapViewModelDeviceModuleTrayToDomain,
} from '../device-module-tray-form/device-module-tray-form.mapping';
import {
  mapAppLandingPageTrayDomainModelToViewModel,
  mapAppLandingTrayViewModelToDomainModel,
} from '../app-landing-mobile-tray-form/app-landing-mobile-tray-form.mapping';
import { mapDomainMarketingKidsToViewModel, mapViewModelKidsToDomain } from '../marketing-kids-tray-form/marketing-kids-tray-form.mapping';
import {
  mapDomainMarketingStudioToViewModel,
  mapViewModelStudioToDomain,
} from '../marketing-studio-tray-form/marketing-studio-tray-form.mapping';
import {
  mapDomainMarketingTrendingToViewModel,
  mapViewModelTrendingToDomain,
} from '../marketing-trending-tray-form/marketing-trending-tray-form.mapping';
import {
  mapDomainEmailCollectorToViewModel,
  mapViewModelEmailCollectorToDomain,
} from '../marketing-email-collector-tray-form/marketing-email-collector-tray-form.mapping';
import { mapCatchUpTrayDomainModelToViewModel, mapCatchUpTrayViewModelToDomainModel } from '../catch-up-tray/catch-up-tray.mapping';
import {
  mapMarketingVideoTrayDomainModelToViewModel,
  mapMarketingVideoTrayViewModelToDomainModel,
} from '../marketing-video-tray-form/marketing-video-tray.mapping';
import {
  mapMarketingBarTrayDomainModelToViewModel,
  mapMarketingBarTrayViewModelToDomainModel,
} from '../marketing-bar-tray/marketing-bar-tray-mapping';
import {
  mapLandscapeTrayDomainModelToViewModel,
  mapLandscapeTrayViewModelToDomainModel,
} from '../landscape-tray-form/landscape-tray-form.mapping';
import {
  mapCategoryTrayDomainModelToViewModel,
  mapCategoryTrayViewModelToDomainModel,
} from '../category-tray-form/category-tray-form.mapping';
import {
  mapOndemandBannerTrayDomainModelToViewModel,
  mapOndemandBannerTrayViewModelToDomainModel,
} from '../ondemand-banner-tray-form/ondemand-banner-tray-form.mapping';
import { PageVersionStatusApiModel } from '@cxb/editor-stb-api';

@Injectable()
export class CreateTrayScreenService implements OnDestroy {
  private readonly destroyed$: Subject<void> = new Subject<void>();
  public readonly pageType: PageType;
  private loggedInUserSegmentIds: string[];
  private platformIds: Record<PlatformType, string>;
  private genres: Genre[];

  public userSegmentOptions$: Observable<MultiSelectOption[]> = this.rootStore.select(selectCxbUserSegments).pipe(
    filter((userSegments) => !!userSegments?.length),
    map((userSegments: UserSegment[]) => this.mapUserSegmentsToViewModel(userSegments))
  );

  public countryOptions$: Observable<MultiSelectOption[]> = this.rootStore.select(selectCxbCountries).pipe(
    filter((countries) => !!countries?.length),
    map((countries: Country[]) => this.mapCountryToMultiSelectOption(countries))
  );

  public platformOptions$: Observable<MultiSelectOption[]> = this.rootStore.select(selectCxbPlatforms).pipe(
    filter((platforms) => !!platforms.length),
    map((platforms) => this.mapPlatformsToMultiSelectOption(platforms))
  );

  public vm$: Observable<CreateTrayScreenViewModel> = this.pageEditStore.select(selectCxbPageVersion).pipe(
    filter(isTruthy),
    map((pageVersion) => this.mapDomainPageToViewModel(pageVersion))
  );

  public trayFormVm$: Observable<TrayFormViewModel> = this.store.select(selectCxbPageItem).pipe(
    filter(isTruthy),
    map((item) => this.mapPageItemToTrayFormViewModel(item))
  );

  public isEdit$: Observable<boolean> = combineLatest({
    trayId$: this.rootStore.select(selectRouteParam('trayId')),
    trayType$: this.rootStore.select(selectRouteParam('trayType')),
  }).pipe(
    takeUntil(this.destroyed$),
    map(({ trayId$, trayType$ }) => !!trayId$ && trayType$ !== 'tray')
  );

  public isReadonly$: Observable<boolean> = this.pageEditStore.select(selectCxbPageVersion).pipe(
    takeUntil(this.destroyed$),
    map((pageVersion) => pageVersion?.pageStatus === PageVersionStatusApiModel.Published)
  );

  constructor(
    private readonly pageEditStore: Store<PageEditAppState>,
    private readonly store: Store<AppState>,
    private readonly previewStore: Store<PreviewFeatureState>,
    private readonly rootStore: Store<RootAppState & RouterState>,
    private readonly route: ActivatedRoute
  ) {
    const pageType = this.route.snapshot.data?.pageType;

    if (!(pageType instanceof PageType)) {
      throw Error('Page type is required');
    }

    this.pageType = pageType;

    this.rootStore
      .select(selectRouteParam('pageVersionId'))
      .pipe(
        takeUntil(this.destroyed$),
        filter(isTruthy),
        withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
        tap(([pageVersionId, selectedTenant]) =>
          this.pageEditStore.dispatch(
            loadCxbPageEditScreen.sourceAction({
              pageVersionId,
              userSegments: undefined,
              countryCodes: undefined,
              pageType: this.pageType,
              selectedTenant,
            })
          )
        )
      )
      .subscribe();

    combineLatest([this.rootStore.select(selectRouteParam('trayType')), this.rootStore.select(selectRouteParam('trayId'))])
      .pipe(
        takeUntil(this.destroyed$),
        filter(([trayType, pageVersionId]) => isTruthy(trayType) && trayType !== 'tray' && isTruthy(pageVersionId)),
        tap(([trayType, trayId]) => {
          const pageItemType = TrayTypeBridge.parseFromRouteParam(trayType).pageItemType;
          this.store.dispatch(loadCxbPageItem({ id: trayId, discriminatorType: pageItemType, pageType: this.pageType }));
        })
      )
      .subscribe();

    this.rootStore
      .select(selectCxbUserSegments)
      .pipe(
        takeUntil(this.destroyed$),
        filter(isTruthy),
        tap((userSegments: UserSegment[]) => {
          this.loggedInUserSegmentIds = userSegments
            .filter((userSegment) => userSegment.name !== 'anonymous')
            .map((loggedInUser) => loggedInUser.id);
        })
      )
      .subscribe();

    this.rootStore
      .select(selectCxbPlatforms)
      .pipe(
        takeUntil(this.destroyed$),
        filter(isTruthy),
        tap((platforms: Platform[]) => {
          // @ts-ignore
          this.platformIds = Object.fromEntries(platforms.map((res) => [res.name, res.id]));
        })
      )
      .subscribe();

    this.rootStore
      .select(selectGenres)
      .pipe(
        takeUntil(this.destroyed$),
        filter(isTruthy),
        tap((genres: Genre[]) => {
          this.genres = genres;
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.store.dispatch(clearCxbCreateTrayScreenState());
  }

  public createTray(tray: TrayFormViewModel, pageVersionId: string): void {
    const pageItem = this.mapTrayFormViewModelToDomain(tray);
    this.store.dispatch(createCxbPageItem({ pageItem, pageVersionId, pageType: this.pageType }));
  }

  public updateTray(tray: TrayFormViewModel, pageVersionId: string): void {
    const pageItem = this.mapTrayFormViewModelToDomain(tray);
    this.store.dispatch(updateCxbPageItem({ pageItem, pageVersionId, pageType: this.pageType }));
  }

  public deleteTray(tray: TrayFormViewModel, pageVersionId: string): void {
    this.store.dispatch(
      deleteCxbPageItem({
        pageVersionId,
        id: tray.id,
        pageType: this.pageType,
        trayType: TrayTypeBridge.parseFromFormType(tray.type).pageItemType,
      })
    );
  }

  private mapTrayFormViewModelToDomain(tray: TrayFormViewModel): PageItem {
    const trayType = tray.type;
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (tray.type) {
      case TrayFormViewModelType.TopTenTray:
      case TrayFormViewModelType.PortraitTray:
        return mapCommonTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.CollectionTray:
        return mapCollectionTrayViewModelToDomainModel(tray as CollectionTrayFormViewModel, this.platformIds);
      case TrayFormViewModelType.WatchlistCarousel:
      case TrayFormViewModelType.ContinueWatchingCarousel:
        return mapBuiltInTrayFormViewModelToDomainModel(tray, this.loggedInUserSegmentIds);
      case TrayFormViewModelType.GenreTray:
        return mapViewModelGenreTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.StudiosTray:
        return mapViewModelStudiosTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.SeasonTray:
        return mapSeasonTrayViewModelToDomainModel(tray as SeasonTrayFormViewModel, this.platformIds);
      case TrayFormViewModelType.SinglePhotoSeasonTray:
        return mapSinglePhotoSeasonTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.HeroTray:
        return mapHeroTrayViewModelToDomainModel(tray, this.platformIds);
      case TrayFormViewModelType.CTA:
        return mapViewModelCtaToDomain(tray, this.platformIds);
      case TrayFormViewModelType.USP:
        return mapViewModelUspToDomain(tray, this.platformIds);
      case TrayFormViewModelType.MarketingHero: // AXB-1138
        return mapViewModelMarketingHeroToDomain(tray, this.platformIds);
      case TrayFormViewModelType.AppLandingCotvTray: // TODO
      case TrayFormViewModelType.AppLandingMobileTray:
        return mapAppLandingTrayViewModelToDomainModel(tray, this.platformIds);
      case TrayFormViewModelType.DetailedPageMarketingBanner: // AXB-1132
        return mapViewModelDetailedPageMarketingBannerToDomain(tray, this.platformIds);
      case TrayFormViewModelType.RedeemPartnerVoucherTray:
        return mapViewModelRedeemPartnerVoucherTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.RedeemGenericVoucherTray:
        return mapViewModelRedeemGenericVoucherTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.FaqTray:
        return mapViewModelFaqTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.TopPicks: // AXB-1061
      case TrayFormViewModelType.TopPicksMovies: // AXB-1062
      case TrayFormViewModelType.Trending:
      case TrayFormViewModelType.TrendingMovies: // AXB-930
      case TrayFormViewModelType.TrendingSeries: // AXB-931
      case TrayFormViewModelType.TrendingWithLayout: // AXB-929
      case TrayFormViewModelType.BecauseYouWatched:
      case TrayFormViewModelType.PeopleLikeYouWatch: // AXB-926
      case TrayFormViewModelType.PeopleLikeYouWatchMovies: // AXB-927
      case TrayFormViewModelType.PeopleLikeYouWatchSeries: // AXB-928
      case TrayFormViewModelType.BecauseYouWatchGenreTray:
      case TrayFormViewModelType.TopPicksSeries: // AXB-926
      case TrayFormViewModelType.DontMiss: // AXB-933
      case TrayFormViewModelType.TopPicksDownloadable:
      case TrayFormViewModelType.TopPicksMoviesDownloadable:
      case TrayFormViewModelType.TopPicksSeriesDownloadable:
      case TrayFormViewModelType.PeopleLikeYouWatchDownloadable:
      case TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable:
      case TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable:
      case TrayFormViewModelType.MostDownloadedMovies:
      case TrayFormViewModelType.MostDownloadedSeries:
      case TrayFormViewModelType.Editorial:
        return mapRecommendationTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.MyFavouritesOnLiveTV: // AXB-71
        return mapFavouriteChannelsTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.AppsAndGames: // AXB-70§
        return mapAppsAndGamesTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.RecentlyWatchedTV: // AXB-77
        return mapRecentlyWatchedTVTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.LiveTVReminders: // AXB-84
        return mapLiveTVRemindersTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.TVGuide: // AXB-78
        return mapTVGuideTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.TopTenOnOSNPlusPosterArt: // AXB-82
        return mapTopTenOnOSNPlusPosterArtTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.TitleImagePortraitTray: // AXB-83
        return mapTitleImagePortraitTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.HeroSpotlightTray: // AXB-67, AXB-68
        return mapHeroSpotlightTrayViewModelToDomainModel(tray, this.platformIds);
      case TrayFormViewModelType.MyListTray: // AXB-80
        return mapMyListTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.ContinueWatchingTray: // AXB-79
        return mapContinueWatchingTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.CarouselBannerTray: // AXB-88
        return mapCarouselBannerTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.BannersThree: // AXB-87
        return mapNewCarouselTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.FullscreenTray: // AXB-72
        return mapFullscreenTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.StreamingContentTray: // AXB-81
        return mapStreamingContentTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.UpcomingOnSpecificTvChannelTray: // AXB-83
        return mapUpcomingOnSpecificTvChannelTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.ComingSoonForLiveTvTray: //AXB-74
        return mapComingSoonForLiveTvTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.TopTenOnLiveTvTray: // AXB-75
        return mapTopTenOnLiveTvTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.CurrentlyOnTvTray: // AXB-73
        return mapCurrentlyOnTvTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.ChannelLogoTray: // AXB-76
        return mapChannelLogoTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.BannersTrayWithCta: //AXB-86
        return mapBannersTrayWithCtaTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.SearchTagsTray: //AXB-172
        return mapSearchTagsTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.RecommendedChannelsTray: //AXB-194
        return mapRecommendedChannelsTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.MarketingBannerTray: //AXB-330
        return mapMarketingBannerTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.MarketingEmailBlocker: //AXB-330
        return mapMarketingEmailBlockerTrayViewModelToDomainModel(tray, this.platformIds);
      case TrayFormViewModelType.SpotlightTray:
        return mapSpotlightTrayViewModelToDomainModel(tray, this.platformIds);
      case TrayFormViewModelType.RedeemCtaTray:
        return mapViewModelRedeemCtaTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.RedeemUspTray:
        return mapViewModelRedeemUspTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.DeviceModuleTray: // AXB-959
        return mapViewModelDeviceModuleTrayToDomain(tray, this.platformIds);
      case TrayFormViewModelType.KIDS:
        return mapViewModelKidsToDomain(tray, this.platformIds);
      case TrayFormViewModelType.STUDIO:
        return mapViewModelStudioToDomain(tray, this.platformIds);
      case TrayFormViewModelType.TRENDING:
        return mapViewModelTrendingToDomain(tray, this.platformIds);
      case TrayFormViewModelType.EMAIL:
        return mapViewModelEmailCollectorToDomain(tray, this.platformIds);
      case TrayFormViewModelType.MarketingVideoTray:
        return mapMarketingVideoTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.MarketingBarTray:
        return mapMarketingBarTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.CatchUpTray:
      case TrayFormViewModelType.ChannelSpecificCatchUpTray:
        return mapCatchUpTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.LandscapeTray:
        return mapLandscapeTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.WatchlistOnDemandTray:
        return mapWatchlistOnDemandTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.PrimeVideoRecommendationsTray:
        return mapPrimeVideoRecommendationsTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.OndemandCategoryTray:
        return mapCategoryTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.OndemandBannerTray:
        return mapOndemandBannerTrayViewModelToDomainModel(tray);
      case TrayFormViewModelType.ContinueWatchingOndemandTray:
        return mapContinueWatchingOndemandTrayViewModelToDomainModel(tray);
      default:
        throw Error(`Tray type ${trayType} is unsupported`);
    }
  }

  private mapPageItemToTrayFormViewModel(item: PageItem): TrayFormViewModel {
    const trayType = item.type;
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (item.type) {
      case 'Hero':
        return mapHeroTrayDomainModelToViewModel(item);
      case 'MarketingCta':
        return mapDomainCTAToViewModel(item);
      case 'MarketingUsp':
        return mapDomainMarketingUspToViewModel(item);
      case 'MarketingHero':
        return mapDomainMarketingHeroToViewModel(item);
      case 'DetailedPageMarketingBanner':
        return mapDomainDetailedPageMarketingBannerToViewModel(item);
      case 'RedeemPartnerVoucherTray': //AXB-948
        return mapDomainRedeemPartnerVoucherTrayToViewModel(item);
      case 'RedeemGenericVoucherTray': //AXB-1175
        return mapDomainRedeemGenericVoucherTrayToViewModel(item);
      case 'TopTenTray':
      case 'PortraitTray':
        return mapCommonTrayDomainModelToViewModel(item);
      case 'FaqTray':
        return mapDomainFaqTrayToViewModel(item);
      case 'CollectionTray':
        return mapCollectionTrayDomainModelToViewModel(item);
      case 'SeasonTray':
        return mapSeasonTrayDomainModelToViewModel(item);
      case 'SinglePhotoSeasonTray':
        return mapSinglePhotoSeasonTrayDomainModelToViewModel(item);
      case 'BuiltIn':
        return mapBuiltInTrayDomainModelToViewModel(item); // ??? todo: masik oldala v->d kell meg
      case 'RecentlyWatchedTV':
        return mapRecentlyWatchedTVTrayDomainModelToViewModel(item);
      case 'CurrentlyOnTvTray':
        return mapCurrentlyOnTvTrayDomainModelToViewModel(item);
      case 'ChannelLogoTray':
        return mapChannelLogoTrayDomainModelToViewModel(item);
      case 'TitleImagePortraitTray':
        return mapTitleImagePortraitTrayDomainModelToViewModel(item);
      case 'SearchTagsTray':
        return mapSearchTagsTrayDomainModelToViewModel(item);
      case 'StreamingContentTray':
        return mapStreamingContentTrayDomainModelToViewModel(item);
      case 'TopTenOnLiveTvTray':
        return mapTopTenOnLiveTvTrayDomainModelToViewModel(item);
      case 'ComingSoonForLiveTvTray':
        return mapComingSoonForLiveTvTrayDomainModelToViewModel(item);
      case 'BannersTrayWithCta':
        return mapBannersTrayWithCtaTrayDomainModelToViewModel(item);
      case 'TopTenOnOSNPlusPosterArtTray':
        return mapTopTenOnOSNPlusPosterArtTrayDomainModelToViewModel(item);
      case 'UpcomingOnSpecificTvChannelTray':
        return mapUpcomingOnSpecificTvChannelTrayDomainModelToViewModel(item);
      case 'RecommendedChannelsTray':
        return mapRecommendedChannelsTrayDomainModelToViewModel(item);
      case 'MarketingBannerTray':
        return mapMarketingBannerTrayDomainModelToViewModel(item);
      case 'Recommendation':
        return mapRecommendationTrayDomainModelToViewModel(item);
      case 'AppsAndGamesInstalledApps':
        return mapAppsAndGamesInstalledAppsTrayDomainModelToViewModel(item);
      case 'AppsAndGamesRecommendedApps':
        return mapAppsAndGamesRecommendedAppsTrayDomainModelToViewModel(item);
      case 'HeroSpotlight':
        return mapHeroSpotlightTrayDomainModelToViewModel(item);
      case 'FullscreenTray':
        return mapFullscreenTrayDomainModelToViewModel(item);
      case 'CarouselTray':
        return mapNewCarouselTrayDomainModelToViewModel(item);
      case 'CarouselBannerTray':
        return mapCarouselBannerTrayDomainModelToViewModel(item);
      case 'MarketingEmailBlocker':
        return mapMarketingEmailBlockerTrayDomainModelToViewModel(item);
      case 'SpotlightTray':
        return mapSpotlightTrayDomainModelToViewModel(item);
      case 'GenreTray':
        return mapDomainGenreTrayToViewModel(item, this.genres);
      case 'StudiosTray':
        return mapDomainStudiosTrayToViewModel(item, this.genres);
      case 'RedeemCtaTray':
        return mapDomainRedeemCtaTrayToViewModel(item);
      case 'RedeemUspTray':
        return mapDomainRedeemUspTrayToViewModel(item);
      case 'DeviceModuleTray':
        return mapDomainDeviceModuleTrayToViewModel(item);
      case 'AppLandingPageTray':
        return mapAppLandingPageTrayDomainModelToViewModel(item);
      case 'MarketingKids':
        return mapDomainMarketingKidsToViewModel(item);
      case 'MarketingStudio':
        return mapDomainMarketingStudioToViewModel(item);
      case 'MarketingTrending':
        return mapDomainMarketingTrendingToViewModel(item);
      case 'MarketingEmailCollector':
        return mapDomainEmailCollectorToViewModel(item);
      case 'MarketingVideoTray':
        return mapMarketingVideoTrayDomainModelToViewModel(item);
      case 'MarketingBarTray':
        return mapMarketingBarTrayDomainModelToViewModel(item);
      case 'CatchUpTray':
      case 'ChannelSpecificCatchUpTray':
        return mapCatchUpTrayDomainModelToViewModel(item);
      case 'LandscapeTray':
        return mapLandscapeTrayDomainModelToViewModel(item);
      case 'OndemandCategoryTray':
        return mapCategoryTrayDomainModelToViewModel(item);
      case 'OndemandBannerTray':
        return mapOndemandBannerTrayDomainModelToViewModel(item);
      default:
        throw Error(`Tray type ${trayType} is unsupported`);
    }
  }

  private mapUserSegmentsToViewModel(userSegments: UserSegment[]): MultiSelectOption[] {
    return userSegments.reduce((prev: MultiSelectOption[], curr: UserSegment) => {
      switch (curr.name) {
        case UserSegmentType.Anonymous:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.Anonymous });
        case UserSegmentType.Prospect:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.Prospect });
        case UserSegmentType.Normal:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.Normal });
        case UserSegmentType.PendingChurn:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.PendingChurn });
        case UserSegmentType.Dunning:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.Dunning });
        case UserSegmentType.Churned:
          return prev.concat({ id: curr.id, label: UserSegmentLabel.Churned });
        default:
          return prev;
      }
    }, []);
  }

  private mapDomainPageToViewModel(pageVersion: PageVersion): CreateTrayScreenViewModel {
    return { title: pageVersion.englishTitle, readonly: pageVersion.pageStatus === 'PUBLISHED' };
  }

  private mapCountryToMultiSelectOption(countries: Country[]): MultiSelectOption[] {
    return countries.map((country) => ({ id: country.id, label: country.name }));
  }

  private mapPlatformsToMultiSelectOption(platforms: Platform[]): MultiSelectOption[] {
    return platforms.map((platform) => ({ id: platform.id, label: platform.name }));
  }

  public updatePreview(trayFormViewModel: TrayFormViewModel) {
    if (!trayFormViewModel?.type) {
      this.previewStore.dispatch(PageItemPreviewActions.clearState());
      return;
    }

    const pageItem = this.mapTrayFormViewModelToDomain(trayFormViewModel);

    this.previewStore.dispatch(PageItemPreviewActions.loadPreview({ pageItem }));
  }
}
